/**
 * @file Admin-gated list page for HI alerts and reviews.
 * @module pages/admin/hi-alerts/HiAlertReviewPage
 *
 * B1.3H-3R-3 — UC.015 / HI_015_005818. A thin operator feed of the same-patient
 * `ihi-revalidation-review` type. Rows show only category-only projection fields,
 * lifecycle timestamps and the internal patient id (id-only — no demographic
 * label lookup). A row opens a read-only detail drawer; the amend/resubmit action
 * is offered only for an open amendable no-match review (UC.015 `no_match`, the three
 * UC.010 registration no-match reasons since CU-14ygtkj8z3m, and the UC.015 Medicare
 * Type 1/2/3 and address Type 6 no-match reviews since CU-14ygtkj98pb), from the
 * row and the drawer. There is no auto-resolution or supersession workflow.
 *
 * B1.3H-3W-5D — adds a Severity column carrying the backend-projected severity
 * category, through the shared display contract in `hiAlertLabels`. The backend
 * derives severity from the persisted bounded review reason (`authentication#128`);
 * this page only presents the category it receives and never re-derives it from the
 * reason, requirement id or any returned status.
 *
 * B3.3 (CU-14ygtkj8z2x) — lists the shared three-type feed; rows are keyed and
 * navigated by type + id, conflict types show no severity, amend stays revalidation-only.
 */

import React, { useState } from "react";
import { Button, Space } from "antd";
import { SearchOutlined, EyeOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useQuery } from "@tanstack/react-query";

import Layout from "../../../components/Layout";
import PageContainer from "../../../components/shared/PageContainer";
import PageToolbar from "../../../components/shared/PageToolbar";
import DataTable from "../../../components/shared/DataTable";
import StatusTag from "../../../components/shared/StatusTag";

import { listHiAlerts } from "../../../api/admin/hiAlertsApi";
import {
	HiAlertReview,
	HiAlertReviewRef,
	IhiRevalidationReview,
	canAmendReview,
	hiAlertReviewKey,
	isIhiRevalidationReview,
} from "../../../types/admin/hiAlerts";
import {
	formatDateTime,
	hiAlertCategoryLabel,
	hiAlertPatientLabel,
	hiAlertTypeLabel,
	reviewSeverityPresentation,
} from "./hiAlertLabels";
import HiAlertDetailDrawer from "./HiAlertDetailDrawer";
import AmendedSearchModal from "./AmendedSearchModal";
import HiServiceModeNotice from "../../../components/healthcare/HiServiceModeNotice";

const PER_PAGE = 20;

const HiAlertReviewPage: React.FC = () => {
	const [status, setStatus] = useState<string>("open");
	const [page, setPage] = useState<number>(1);

	const [detailRef, setDetailRef] = useState<HiAlertReviewRef | null>(null);
	const [detailOpen, setDetailOpen] = useState<boolean>(false);

	const [amendReview, setAmendReview] = useState<IhiRevalidationReview | null>(null);
	const [amendOpen, setAmendOpen] = useState<boolean>(false);

	const { data, isLoading } = useQuery({
		queryKey: ["hi-alerts", status, page, PER_PAGE],
		queryFn: () => listHiAlerts({ status, page, perPage: PER_PAGE }),
	});

	const rows: HiAlertReview[] = data?.data ?? [];
	const total = data?.pagination?.total_items ?? 0;

	const openDetail = (review: HiAlertReview) => {
		setDetailRef({ type: review.type, id: review.id });
		setDetailOpen(true);
	};

	const openAmend = (review: IhiRevalidationReview) => {
		setAmendReview(review);
		setAmendOpen(true);
	};

	const columns: ColumnsType<HiAlertReview> = [
		{ title: "ID", dataIndex: "id", key: "id", width: 80 },
		{
			title: "Type",
			key: "type",
			width: 170,
			render: (_: unknown, review: HiAlertReview) => hiAlertTypeLabel(review.type),
		},
		{ title: "Requirement", dataIndex: "requirement_id", key: "requirement_id", width: 160 },
		{
			title: "Category",
			key: "category",
			width: 190,
			render: (_: unknown, review: HiAlertReview) => hiAlertCategoryLabel(review),
		},
		{
			// B1.3H-3W-5D — the backend-projected severity, presented through the shared
			// display contract. Only the revalidation review projects a severity; the
			// cross-patient alerts show a neutral dash rather than an invented category.
			title: "Severity",
			key: "severity",
			width: 130,
			render: (_: unknown, review: HiAlertReview) => {
				if (!isIhiRevalidationReview(review)) {
					return "—";
				}
				const severity = reviewSeverityPresentation(review.severity);
				return <StatusTag status={severity.severity} label={severity.label} color={severity.color} />;
			},
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 110,
			render: (value: string) => <StatusTag status={value} label={value === "open" ? "Open" : "Resolved"} />,
		},
		{
			title: "Patient",
			key: "patient",
			width: 210,
			render: (_: unknown, review: HiAlertReview) => hiAlertPatientLabel(review),
		},
		{
			title: "Detected",
			dataIndex: "detected_at",
			key: "detected_at",
			width: 160,
			render: (value: string | null) => formatDateTime(value),
		},
		{
			title: "Actions",
			key: "actions",
			width: 220,
			render: (_: unknown, review: HiAlertReview) => {
				const amendable = canAmendReview(review) ? review : null;
				return (
					<Space onClick={(e) => e.stopPropagation()}>
						<Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(review)}>
							Review
						</Button>
						{amendable && (
							<Button size="small" type="primary" icon={<SearchOutlined />} onClick={() => openAmend(amendable)}>
								Amend search
							</Button>
						)}
					</Space>
				);
			},
		},
	];

	return (
		<Layout>
			<PageContainer title="HI Reviews" subtitle="HI alerts and reviews awaiting operator action">
				{/* CU-14ygtkj8z2z (B3.4): HI Service mode from healthcare, visible while reviewing UC.010/UC.015 alerts. */}
				<HiServiceModeNotice />
				<PageToolbar
					filters={[
						{
							type: "select",
							placeholder: "Filter by status",
							options: [
								{ label: "Open", value: "open" },
								{ label: "Resolved", value: "resolved" },
							],
							value: status,
							onChange: (v) => {
								setStatus(v || "open");
								setPage(1);
							},
							width: 180,
						},
					]}
				/>
				<DataTable
					columns={columns}
					dataSource={rows}
					rowKey={(review: HiAlertReview) => hiAlertReviewKey(review)}
					loading={isLoading}
					onRowClick={openDetail}
					total={total}
					currentPage={page}
					pageSize={PER_PAGE}
					onPageChange={(p) => setPage(p)}
					emptyTitle="No HI alerts or reviews"
					emptyDescription="There are no alerts or reviews to show for this filter"
					fillHeight
				/>
			</PageContainer>

			<HiAlertDetailDrawer
				open={detailOpen}
				reviewRef={detailRef}
				onClose={() => setDetailOpen(false)}
				onAmend={(review) => {
					setDetailOpen(false);
					openAmend(review);
				}}
			/>

			<AmendedSearchModal open={amendOpen} review={amendReview} onClose={() => setAmendOpen(false)} />
		</Layout>
	);
};

export default HiAlertReviewPage;
